import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import {
  VitaTransactionItem,
  VitaTxQuery,
  VitaWalletService,
} from '../../services/vita-wallet.service';
import { VitaMovimientoDetalleComponent } from './detalle-dialog/vita-movimiento-detalle.component';

/** Clasificación de dirección del movimiento por categoría de Vita. */
type Direction = 'in' | 'out' | 'neutral';

const PAYIN_CATEGORIES = new Set(['deposit', 'received', 'cash_back_coupon', 'card_credit_adjustment']);
const PAYOUT_CATEGORIES = new Set([
  'withdrawal', 'sent', 'vita_sent', 'payment', 'transfer', 'service_payment',
  'fee', 'tax', 'card_debit_adjustment', 'card_maintenance', 'card_transaction',
]);

/**
 * Movimientos de la cuenta Vita (pestaña aparte, read-only). Consume el
 * endpoint existente `vita-wallet/transactions/all` (get_all_transactions);
 * NO toca el backend de administración Vita. Lista payins, payouts,
 * exchanges, fees, etc. con filtros server-side y paginación.
 */
@Component({
  selector: 'app-vita-movimientos',
  templateUrl: './vita-movimientos.component.html',
  styleUrls: ['./vita-movimientos.component.sass'],
})
export class VitaMovimientosComponent implements OnInit, OnDestroy {
  loading = true;
  error: string | null = null;

  items: VitaTransactionItem[] = [];
  total = 0;

  // Paginación server-side
  page = 1;
  count = 20;
  readonly pageSizeOptions = [10, 20, 50, 100];

  // Filtros (los que soporta get_all_transactions)
  filtroEstado = '';
  filtroCategoria = '';
  filtroMoneda = '';
  filtroSource = '';
  fechaDesde = '';
  fechaHasta = '';
  montoMin: number | null = null;
  montoMax: number | null = null;
  sort: 'desc' | 'asc' = 'desc';
  /** Texto libre (filtro client-side sobre la página cargada). */
  filtroTexto = '';

  readonly estados = [
    { value: '', label: 'Todos' },
    { value: 'completed', label: 'Completada' },
    { value: 'pending', label: 'Pendiente' },
    { value: 'processed', label: 'Procesada' },
    { value: 'started', label: 'Iniciada' },
    { value: 'denied', label: 'Rechazada' },
  ];

  /** Categorías válidas de Vita (para el selector). */
  readonly categorias = [
    '', 'deposit', 'withdrawal', 'sent', 'received', 'exchange', 'payment',
    'transfer', 'fee', 'tax', 'vita_card', 'service_payment', 'adjustment',
    'cash_back_coupon', 'card_transaction', 'card_maintenance', 'microdeposit',
  ];

  private reload$ = new Subject<void>();
  private sub = new Subscription();

  constructor(
    private vita: VitaWalletService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.sub.add(
      this.reload$.pipe(debounceTime(350)).subscribe(() => this.cargar()),
    );
    this.cargar();
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  private buildQuery(): VitaTxQuery {
    const q: VitaTxQuery = { page: this.page, count: this.count, sort: this.sort };
    if (this.filtroEstado) q.status = this.filtroEstado;
    if (this.filtroCategoria) q.transactions_type = this.filtroCategoria;
    if (this.filtroMoneda) q.currency = this.filtroMoneda.toLowerCase();
    if (this.filtroSource) q.source = this.filtroSource;
    if (this.fechaDesde) q.start_date = this.fechaDesde;
    if (this.fechaHasta) q.end_date = this.fechaHasta;
    if (this.montoMin != null) q.min_amount = this.montoMin;
    if (this.montoMax != null) q.max_amount = this.montoMax;
    return q;
  }

  cargar(): void {
    this.loading = true;
    this.error = null;
    this.sub.add(
      this.vita.listVitaTransactions(this.buildQuery()).subscribe({
        next: (res) => {
          this.items = res.items || [];
          this.total = res.total || 0;
          this.loading = false;
        },
        error: (err) => {
          this.loading = false;
          if (err?.transient) return;
          this.error =
            typeof err === 'string' ? err : 'No se pudieron cargar los movimientos de Vita.';
          this.items = [];
        },
      }),
    );
  }

  /** Filtros que pegan al server → resetean a página 1 y recargan (debounced). */
  onServerFilterChange(): void {
    this.page = 1;
    this.reload$.next();
  }

  onPageSizeChange(): void {
    this.page = 1;
    this.cargar();
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.total / this.count));
  }

  goPage(p: number): void {
    if (p < 1 || p > this.totalPages || p === this.page) return;
    this.page = p;
    this.cargar();
  }

  clearFilters(): void {
    this.filtroEstado = '';
    this.filtroCategoria = '';
    this.filtroMoneda = '';
    this.filtroSource = '';
    this.fechaDesde = '';
    this.fechaHasta = '';
    this.montoMin = null;
    this.montoMax = null;
    this.filtroTexto = '';
    this.sort = 'desc';
    this.page = 1;
    this.cargar();
  }

  /** Filtro de texto client-side sobre la página actual. */
  get visibleItems(): VitaTransactionItem[] {
    const q = this.filtroTexto.trim().toLowerCase();
    if (!q) return this.items;
    return this.items.filter((t) => {
      const a = t.attributes;
      return `${t.id} ${a.order ?? ''} ${a.recipient_email ?? ''} ${a.category ?? ''} ${a.currency ?? ''} ${a.vita_transaction_id ?? ''}`
        .toLowerCase()
        .includes(q);
    });
  }

  get rangeLabel(): string {
    if (this.total === 0) return '0 movimientos';
    const from = (this.page - 1) * this.count + 1;
    const to = Math.min(this.total, from + this.items.length - 1);
    return `${from}–${to} de ${this.total}`;
  }

  // ─── Presentación ───

  direction(category: string): Direction {
    const c = (category || '').toLowerCase();
    if (PAYIN_CATEGORIES.has(c)) return 'in';
    if (PAYOUT_CATEGORIES.has(c)) return 'out';
    return 'neutral';
  }

  dirLabel(category: string): string {
    switch (this.direction(category)) {
      case 'in': return 'Payin';
      case 'out': return 'Payout';
      default: return '—';
    }
  }

  categoriaLabel(category: string): string {
    const map: Record<string, string> = {
      deposit: 'Depósito (payin)',
      withdrawal: 'Retiro (payout)',
      sent: 'Enviado',
      received: 'Recibido',
      vita_sent: 'Transferencia Vita',
      exchange: 'Cambio',
      payment: 'Pago',
      transfer: 'Transferencia',
      fee: 'Comisión',
      tax: 'Impuesto',
      service_payment: 'Pago de servicio',
      adjustment: 'Ajuste',
      cash_back_coupon: 'Cashback',
      microdeposit: 'Microdepósito',
    };
    return map[(category || '').toLowerCase()] || category || '—';
  }

  statusLabel(s: string): string {
    const map: Record<string, string> = {
      completed: 'Completada',
      pending: 'Pendiente',
      processed: 'Procesada',
      started: 'Iniciada',
      denied: 'Rechazada',
    };
    return map[(s || '').toLowerCase()] || s;
  }

  statusBg(s: string): string {
    switch ((s || '').toLowerCase()) {
      case 'completed': return '#15803d';
      case 'processed': return '#16a34a';
      case 'pending':
      case 'started': return '#f59e0b';
      case 'denied': return '#dc2626';
      default: return '#f3f4f6';
    }
  }

  statusColor(s: string): string {
    return this.statusBg(s) === '#f3f4f6' ? '#6b7280' : '#ffffff';
  }

  openDetalle(t: VitaTransactionItem): void {
    this.dialog.open(VitaMovimientoDetalleComponent, {
      data: { tx: t },
      maxHeight: '90vh',
      maxWidth: '95vw',
      minWidth: '520px',
      panelClass: 'aguacate-dialog',
      autoFocus: false,
    });
  }

  refresh(): void {
    this.cargar();
  }

  trackById = (_: number, t: VitaTransactionItem) => t.id;
  trackByStr = (_: number, s: string) => s;
}
